import moment from 'moment'
import _ from 'lodash'
import actions from './calendarEventActions'
import {
  CalendarEventAssistantState,
  CalendarEventState
} from '@/types/application-state'
import * as getterTypes from '@/store/getter-types'
import * as mutationTypes from '@/store/mutation-types'
import { ShiftEditorPlanningPayload } from '@/model/planning/ShiftEditorPlanningPayload'
import { CompanyFilterValue, PlanningAssistantPeriodicityPayload } from '@/types/payload'
import { EmployeeAvailableForPlanning } from '@/model/planning/EmployeeAvailableForPlanning'
import { Employee } from '@/model/companyStructure/Employee'
import { PeriodicityPlannedCalendarEventEmployee } from '@/types'
import { CalendarEventSerie, KalenderEventsCategorize } from '@/types/service-response'
import { CalendarEventEmployee } from '@/model/calendarEvent/CalendarEventEmployee'

const defaultCalendarEventState = (): CalendarEventAssistantState => {
  const startDate = moment().startOf('day').add(8, 'hour')
  const endDate = moment(startDate).add(8, 'hour')

  return {
    initialEmployeeList: [],

    companyStructure: {
      profitCenterList: [],
      departmentList: [],
      planningGroupList: [],
      employeeList: []
    },

    filterValue: {
      profitCenter: undefined,
      department: undefined,
      planningGroup: undefined,
      employee: undefined
    },

    calendarEventItem: undefined,
    startDate: startDate.toDate(),
    endDate: endDate.toDate(),

    taskList: [],
    employeeForPlaningList: [],
    filteredEmployeesByDepartment: [],

    periodicity: {
      isUnique: true,
      weekDayList: [],
      repeatEveryNthWeek: 1,
      expireAfterRepetitions: 0,
      expireAfterDate: undefined
    },

    plannedEmployeeList: [],
    periodicityPlannedEmployeeList: [],
    isEditingSerie: false,
    periodicitySeriesId: undefined,
    periodicitySerieData: undefined
  }
}

const state: CalendarEventState = {
  calendarEventAssistant: defaultCalendarEventState()
}

const mutations = {

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_RESET_STATE_MUTATION] (state: CalendarEventState): void {
    Object.assign(state.calendarEventAssistant, defaultCalendarEventState())
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_DATA_FROM_PAYLOAD_MUTATION] (state: CalendarEventState, payload: ShiftEditorPlanningPayload): void {
    state.calendarEventAssistant.initialEmployeeList = payload.employeeList
    state.calendarEventAssistant.companyStructure = payload.companyStructure
    state.calendarEventAssistant.filterValue = payload.filterValue

    if (payload.eventTimePeriod) {
      state.calendarEventAssistant.startDate = payload.eventTimePeriod.start
      state.calendarEventAssistant.endDate = payload.eventTimePeriod.end
    }
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_START_DATE_MUTATION] (state: CalendarEventState, payload: Date): void {
    state.calendarEventAssistant.startDate = payload
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_END_DATE_MUTATION] (state: CalendarEventState, payload: Date): void {
    state.calendarEventAssistant.endDate = payload
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_CALENDAR_EVENT_ITEM_MUTATION] (state: CalendarEventState, payload: KalenderEventsCategorize | undefined): void {
    state.calendarEventAssistant.calendarEventItem = payload
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_EMPLOYEE_LIST_FOR_PLANNING_MUTATION] (state: CalendarEventState, payload: EmployeeAvailableForPlanning[]): void {
    state.calendarEventAssistant.employeeForPlaningList = _.cloneDeep(payload)
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_PLANNED_EMPLOYEE_LIST_MUTATION] (state: CalendarEventState, payload: CalendarEventEmployee[]): void {
    state.calendarEventAssistant.plannedEmployeeList = _.cloneDeep(payload)
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_PERIODICITY_PLANNED_EMPLOYEE_LIST_MUTATION] (state: CalendarEventState, payload: PeriodicityPlannedCalendarEventEmployee[]): void {
    state.calendarEventAssistant.periodicityPlannedEmployeeList = _.cloneDeep(payload)
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_FILTERED_EMPLOYEES_BY_DEPARTMENT_MUTATION] (state: CalendarEventState, payload: Employee[]): void {
    state.calendarEventAssistant.filteredEmployeesByDepartment = _.cloneDeep(payload)
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_PERIODICITY_MUTATION] (state: CalendarEventState, payload: PlanningAssistantPeriodicityPayload): void {
    state.calendarEventAssistant.periodicity = _.cloneDeep(payload)
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_IS_EDITING_SERIE_MUTATION] (state: CalendarEventState, payload: boolean): void {
    state.calendarEventAssistant.isEditingSerie = _.cloneDeep(payload)
  },

  [mutationTypes.CALENDAR_EVENT_ASSISTANT_SET_PERIODICITY_SERIE_MUTATION] (state: CalendarEventState, payload: CalendarEventSerie[] | undefined): void {
    state.calendarEventAssistant.periodicitySerieData = _.cloneDeep(payload)
  }
}

const getters = {

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_INITIAL_EMPLOYEE_LIST] (state: CalendarEventState): Employee[] {
    return state.calendarEventAssistant.initialEmployeeList
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_START_DATE] (state: CalendarEventState): Date {
    return state.calendarEventAssistant.startDate
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_END_DATE] (state: CalendarEventState): Date {
    return state.calendarEventAssistant.endDate
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_COMPANY_FILTER] (state: CalendarEventState): CompanyFilterValue {
    return state.calendarEventAssistant.filterValue
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_PERIODICITY] (state: CalendarEventState): PlanningAssistantPeriodicityPayload {
    return state.calendarEventAssistant.periodicity
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_PLANNED_EMPLOYEE_LIST] (state: CalendarEventState): CalendarEventEmployee[] {
    return state.calendarEventAssistant.plannedEmployeeList
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_PERIODICITY_PLANNED_EMPLOYEE_LIST] (state: CalendarEventState): PeriodicityPlannedCalendarEventEmployee[] {
    return state.calendarEventAssistant.periodicityPlannedEmployeeList
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_EMPLOYEE_LIST_FOR_PLANNING] (state: CalendarEventState): EmployeeAvailableForPlanning[] {
    return state.calendarEventAssistant.employeeForPlaningList
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_CALENDAR_EVENT_ITEM] (state: CalendarEventState): KalenderEventsCategorize | undefined {
    return state.calendarEventAssistant.calendarEventItem
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_IS_EDITING_SERIE] (state: CalendarEventState): boolean {
    return state.calendarEventAssistant.isEditingSerie
  },

  [getterTypes.CALENDAR_EVENT_ASSISTANT_GET_PERIODICITY_SERIE] (state: CalendarEventState): CalendarEventSerie[] | undefined {
    return state.calendarEventAssistant.periodicitySerieData
  }
}

const calendarEventModule = {
  state,
  getters,
  mutations,
  actions
}

export default calendarEventModule
